import {createContext, type PropsWithChildren, useContext, useEffect, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {
  allCategoriesOptionID as allModelsCategoryOptionID,
  getDefaultCategory as getModelsDefaultCategory,
} from '../utilities/model-filter-options'
import {useSearchResults} from './SearchResultsContext'
import {useModelsSearchFilters} from '../hooks/use-models-search-filters'

interface CategoryContextType {
  category: string | null
  setCategory: (category: string | null) => void
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined)

export function useCategory() {
  const context = useContext(CategoryContext)
  if (!context) throw new Error('useCategory must be used within a CategoryProvider')
  return context
}

export function CategoryProvider({children}: PropsWithChildren) {
  const [searchParams] = useSearchParams()
  const {searchResults} = useSearchResults()
  const {
    data: {categories},
  } = useModelsSearchFilters()

  // We should ensure that users can't put arbitrary strings in the URL and have them displayed in the search
  // dropdowns. Since category is shared between listing types, we can only validate it against the allowlist
  // if type === model.
  const initialCategory = useMemo(() => {
    if (searchParams.get('type') === 'models') {
      return getModelsDefaultCategory(searchParams, searchResults.parsedQuery, categories)
    }
    if (searchParams.has('type')) return searchParams.get('category')
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories])

  const [category, setCategory] = useState<string | null>(initialCategory)
  const value = useMemo(() => ({category, setCategory}), [category, setCategory])

  // Once the list of valid categories has loaded for Models, the category specified in the query string or other URL
  // parameter may now be considered valid, so we should update the selected/active category.
  useEffect(() => {
    const isDefaultCategorySelection = category === allModelsCategoryOptionID || !category
    if (isDefaultCategorySelection && initialCategory !== allModelsCategoryOptionID) {
      setCategory(initialCategory)
    }
  }, [initialCategory, category])

  return <CategoryContext value={value}>{children}</CategoryContext>
}

try{ CategoryContext.displayName ||= 'CategoryContext' } catch {}
try{ CategoryProvider.displayName ||= 'CategoryProvider' } catch {}